import React, { useState, useEffect } from 'react';
import { adminAPI } from '../api/userAPI';
import { ConfirmDialog, InlineNotice } from './shared/UIComponents';

const AdminPanel = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [notice, setNotice] = useState(null);
  const [pendingActivation, setPendingActivation] = useState(null);

  const loadPendingUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const users = await adminAPI.getPendingUsers();
      setPendingUsers(users);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load pending users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingUsers();
  }, []);

  const activateUser = async (userId, userEmail) => {
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      await adminAPI.activateUser(userId);
      await loadPendingUsers(); // Refresh the list
      setNotice({ type: 'success', message: `User ${userEmail} activated successfully.` });
    } catch (err) {
      setNotice({ type: 'error', message: `Failed to activate user: ${err.message}` });
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: false }));
      setPendingActivation(null);
    }
  };

  if (loading) {
    return <div className="admin-panel">Loading pending users...</div>;
  }

  if (error) {
    return <div className="admin-panel error">Error: {error}</div>;
  }

  return (
    <div className="admin-panel">
      <h2>Admin Panel - Pending User Accounts</h2>
      <InlineNotice type={notice?.type} message={notice?.message} />
      
      {pendingUsers.length === 0 ? (
        <p>No pending users found.</p>
      ) : (
        <div className="pending-users-list">
          <h3>Users Pending Verification ({pendingUsers.length})</h3>
          {pendingUsers.map((user) => (
            <div key={user.id} className="pending-user-card">
              <div className="user-info">
                <h4>{user.full_name}</h4>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Organization:</strong> {user.organization || 'Not specified'}</p>
                <p><strong>Created:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
                <p><strong>Status:</strong> <span className="status-pending">Pending Verification</span></p>
              </div>
              <div className="user-actions">
                <button
                  onClick={() => setPendingActivation({ userId: user.id, userEmail: user.email })}
                  disabled={actionLoading[user.id]}
                  className="activate-button"
                >
                  {actionLoading[user.id] ? 'Activating...' : 'Activate Account'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="admin-actions">
        <button onClick={loadPendingUsers} className="refresh-button">
          Refresh List
        </button>
      </div>
      <ConfirmDialog
        open={Boolean(pendingActivation)}
        title="Activate account?"
        message={pendingActivation ? `Activate ${pendingActivation.userEmail}?` : ''}
        confirmLabel="Activate"
        onConfirm={() => activateUser(pendingActivation.userId, pendingActivation.userEmail)}
        onCancel={() => setPendingActivation(null)}
        loading={Boolean(pendingActivation && actionLoading[pendingActivation.userId])}
      />
    </div>
  );
};

export default AdminPanel;
